import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn } from "lucide-react";

/**
 * Convert the source image + pixel crop region into a JPEG Blob.
 * Output is capped at maxDim long-edge so producers can't accidentally
 * push 8MB phone photos to the storage bucket.
 */
async function cropToBlob(imageSrc, pixelCrop, maxDim = 1600) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const scale = Math.min(1, maxDim / Math.max(pixelCrop.width, pixelCrop.height));
  const w = Math.round(pixelCrop.width * scale);
  const h = Math.round(pixelCrop.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, w, h);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

/**
 * Drag-to-crop dialog. Shown after the user picks a file in VendorEditDialog —
 * the buyer-facing producers grid renders covers at 16:9 and avatars at 1:1,
 * so we let producers frame their hero shot exactly.
 *
 * Props:
 *   - imageSrc: a blob URL of the chosen file
 *   - aspect: number (e.g., 16/9 or 1)
 *   - shape: "rect" | "round"
 *   - onConfirm(blob): receives the cropped JPEG blob
 *   - onCancel()
 */
export const ImageCropDialog = ({ imageSrc, aspect = 16 / 9, shape = "rect", onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [working, setWorking] = useState(false);

  const onCropComplete = useCallback((_a, cropAreaPixels) => {
    setPixelCrop(cropAreaPixels);
  }, []);

  const confirm = async () => {
    if (!pixelCrop) return;
    setWorking(true);
    try {
      const blob = await cropToBlob(imageSrc, pixelCrop, shape === "round" ? 800 : 1600);
      onConfirm(blob);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={!!imageSrc} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl bg-bone" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {shape === "round" ? t("crop.titleAvatar") : t("crop.titleCover")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink-soft -mt-2 mb-3">{t("crop.hint")}</p>

        <div className="relative w-full h-[420px] bg-black/85 rounded-sm overflow-hidden" data-testid="crop-canvas">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={shape}
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
            />
          )}
        </div>

        <div className="mt-5 flex items-center gap-4">
          <ZoomIn className="w-4 h-4 text-ink-muted" />
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.05}
            onValueChange={(v) => setZoom(v[0])}
            className="flex-1"
            data-testid="crop-zoom"
          />
          <span className="text-xs font-mono text-ink-muted w-12 text-right">{zoom.toFixed(2)}×</span>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" className="rounded-sm" onClick={onCancel} data-testid="crop-cancel">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={confirm}
            disabled={working || !pixelCrop}
            className="bg-olive hover:bg-olive-dark text-bone rounded-sm"
            data-testid="crop-confirm"
          >
            {working ? t("vendorEdit.uploading") : t("crop.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog;
